// @flow

import React, { Component } from "react";
import * as R from "ramda";
import { connect } from "react-redux";

import ProfileSetup from "./ProfileSetup";
import ServerError from "./ServerError";
import FormContainerWithLogo from "src/components/auth/FormContainerWithLogo";
import LoaderComponent from "src/components/auth/Loader";

import { getAuthProvider } from "src/api/auth";
import { join } from "src/actions/signup";
import { getSignUpError } from "src/reducers";

import type { AppState, UID } from "src/types";

type Props = {
  joiningOrg: boolean,
  joinUserError: any,
  uid: UID,
  profilePhotoError: any,
  currentName: string,
  currentEmail: string,
  displayNameError: Object,
  join: typeof join,
};

type State = {
  displayName: string,
  password: string,
  email: string,
  files: Array<Object>,
  autopopulate: boolean,
  tenantId: string | null,
  isLoading: boolean,
};

class Join extends Component<Props, State> {
  state = {
    displayName: "",
    password: "",
    files: [],
    email: "",
    autopopulate: false,
    tenantId: null,
    isLoading: true,
  };

  componentDidMount() {
    const url = new URL(window.location);
    const email = url.searchParams.get("email") || "";
    const displayName = url.searchParams.get("inviteeName");
    const orgId = parseInt(url.searchParams.get("orgId"), 10);

    sessionStorage.setItem("lastOrg", `${orgId}`);
    const { currentName } = this.props;

    this.setState({
      email,
      displayName: currentName || displayName || "",
    });

    getAuthProvider(email, `${orgId}`)
      .then((authProvider) => {
        this.setState({
          tenantId: authProvider.tenantId,
          isLoading: false,
        });
      })
      .catch((err) => {
        // Show the password setting form for new users
        if (err.status === 403) {
          this.setState({
            tenantId: null,
            isLoading: false,
          });
        }
      });
  }

  componentDidUpdate(prevProps: Props) {
    const { currentName, currentEmail } = this.props;
    if (
      prevProps.currentEmail !== currentEmail &&
      currentEmail &&
      !currentName
    ) {
      this.setState({
        displayName: R.head((currentEmail || "").split("@")),
      });
    }
    if (prevProps.currentName !== currentName && currentName) {
      this.setState({
        displayName: currentName,
      });
    }
  }

  handleSubmit = (e: any) => {
    e.preventDefault();
    const { files } = this.state;
    const { join } = this.props;
    const profilePicture = !R.isEmpty(files) ? files[0] : null;

    const url = new URL(window.location);
    const orgId = parseInt(url.searchParams.get("orgId"), 10);

    join({
      email: this.state.email,
      displayName: this.state.displayName,
      password: this.state.password,
      profilePicture,
      orgId,
    });
  };

  handleChange = (e: Object) => {
    const { name, value } = e.target;
    this.setState({
      [name]: value,
    });
  };

  onDrop = (files: Array<Object>) => {
    const filesWithUrl = files.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      }),
    );
    this.setState({
      files: filesWithUrl,
    });
  };

  removeFile = (files: Array<Object>) => {
    this.setState({ files });
  };

  render() {
    const { files } = this.state;
    const imageUrl = !R.isEmpty(files) ? R.prop("preview")(files[0]) : "";
    const { joinUserError, displayNameError } = this.props;

    // Show the initial loader while the app fetches auth provider details
    // Once fetched, it should show the loader if SSO authentication is expected
    // otherwise, show the "join" form
    return (
      <FormContainerWithLogo>
        {this.state.isLoading ? (
          <LoaderComponent customMessage="Setting up Unifize for you" />
        ) : this.state.tenantId !== null ? (
          <LoaderComponent customMessage="Verifying credentials" />
        ) : joinUserError || displayNameError ? (
          <ServerError
            joinError={joinUserError.message}
            displayNameError={displayNameError.message}
          />
        ) : (
          <ProfileSetup
            {...{
              ...this.state,
              ...this.props,
              imageUrl,
              handleChange: this.handleChange,
              handleSubmit: this.handleSubmit,
              onDrop: this.onDrop,
              removeFile: this.removeFile,
            }}
          />
        )}
      </FormContainerWithLogo>
    );
  }
}

const mapStateToProps = ({ app }: { app: AppState }) => ({
  joinUserError: getSignUpError("join")(app),
  currentEmail: app.currentUser.email,
  currentName: app.currentUser.displayName,
  joiningOrg: app.signup.joiningOrg,
  uid: app.currentUser.uid,
  displayNameError: getSignUpError("displayName")(app),
  profilePhotoError: getSignUpError("profilePicture")(app),
});

export default connect(mapStateToProps, {
  join,
})(Join);
